import React, { useState, useEffect } from "react";
import { IconX } from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import { useOrderCreateMutation } from "../../../apis/orderApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { populateSenderData } from "../../../utilities/location";
import toast from "react-hot-toast";
import { OrderFormData } from "../../../types/types";
import { extractErrorMessage } from "../../../utilities/apiErrorHandler";
import SenderSection from "./SenderSection";
import ReceiverSection from "./ReceiverSection";
import ShipmentSection from "./ShipmentSection";
import PreviewModal from "./PreviewModal";
import { useClientPermissions } from "../../../apis/profileApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../../features/auth/authSlice";
import ShipmentItemSection from "./ShipmentItemSection";

interface AddOrdersModalsProps {
  onClose: () => void;
  fetchData: () => void;
}

const AddOrdersModal: React.FC<AddOrdersModalsProps> = ({
  onClose,
  fetchData,
}) => {
  const [step, setStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [useDifferentSender, setUseDifferentSender] = useState(false);
  const [isShipmentSubmitted, setIsShipmentSubmitted] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);

  const { mutate: createOrder, isPending: isRequestPending } =
    useOrderCreateMutation();
  const { data: clients } = useMerchantAllListData();

  const { isLoading } = useClientPermissions();
  const permissions = useSelector(selectClientPermissions);

  const {
    register,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
    watch,
    control,
    handleSubmit,
    trigger,
    getFieldState,
  } = useForm<OrderFormData>({
    mode: "onChange",
  });

  const selectedClientId = watch("clientId");
  useEffect(() => {
    const client = clients?.find((c: any) => c.id === selectedClientId);
    if (client?.isNippco === true) {
      setUseDifferentSender(true);
    }
  }, [selectedClientId, clients]);

  /** Collect all field names that must be validated before submit */
  const getAllRequiredFields = (): (keyof import("../../../types/types").OrderFormData | string)[] => {
    const senderFields = useDifferentSender
      ? [
          "sender.name",
          "sender.mobile",
          "sender.email",
          "sender.country",
          "sender.city",
          "sender.district",
          "sender.zipCode",
          "sender.address",
          "sender.nationalAddress",
        ]
      : ["clientId"];

    const receiverFields = [
      "receiver.name",
      "receiver.mobile",
      "receiver.email",
      "receiver.country",
      "receiver.address",
      "receiver.latitude",
      "receiver.longitude",
    ];

    const shipmentFields = [
      "shipment.referenceNumber",
      "shipment.shipmentCost",
      "shipment.paymentType",
      "shipment.numberOfPieces",
      "shipment.totalWeight",
      "shipment.serviceType",
      "shipment.shipmentType",
      "shipment.description",
    ];

    return [...senderFields, ...receiverFields, ...shipmentFields];
  };

  const validateStep = async (): Promise<boolean> => {
    if (step === 1) {
      if (useDifferentSender) {
        const senderValid = await trigger([
          "sender.name",
          "sender.mobile",
          "sender.email",
          "sender.country",
          "sender.city",
          "sender.district",
          "sender.zipCode",
          "sender.address",
          "sender.nationalAddress",
        ] as const);

        const receiverValid = await trigger([
          "receiver.name",
          "receiver.mobile",
          "receiver.email",
          "receiver.country",
        ] as const);

        return senderValid && receiverValid;
      } else {
        const clientValid = await trigger(["clientId"] as const);
        const receiverValid = await trigger([
          "receiver.name",
          "receiver.mobile",
          "receiver.email",
          "receiver.country",
        ] as const);

        return clientValid && receiverValid;
      }
    }
    return true;
  };

  const handleNext = async () => {
    const isValidStep = await validateStep();

    if (!isValidStep) {
      toast.error("Please fill all required fields before proceeding");
      return;
    }

    if (step < 2) {
      setStep(step + 1);
    } else {
      setShowPreview(true);
    }
  };

  /** Run full validation across ALL sections before submitting */
  const handleSubmitWithValidation = async () => {
    if (isSubmittingLocal || isRequestPending) return;
    setIsSubmittingLocal(true);

    try {
      setIsShipmentSubmitted(true);
      const allFields = getAllRequiredFields() as any[];
      const isValid = await trigger(allFields);

      if (!isValid) {
        // Use getFieldState to read live errors after trigger resolves
        const senderErrorFields = useDifferentSender
          ? ["sender.name", "sender.mobile", "sender.email", "sender.country",
             "sender.city", "sender.district", "sender.zipCode", "sender.address", "sender.nationalAddress"]
          : ["clientId"];

        const receiverErrorFields = [
          "receiver.name", "receiver.mobile", "receiver.email",
          "receiver.country", "receiver.address", "receiver.latitude", "receiver.longitude",
        ];

        const shipmentErrorFields = [
          "shipment.referenceNumber", "shipment.shipmentCost", "shipment.paymentType",
          "shipment.numberOfPieces", "shipment.totalWeight", "shipment.serviceType",
          "shipment.shipmentType", "shipment.description",
        ];

        const hasSenderError = senderErrorFields.some((f) => getFieldState(f as any).invalid);
        const hasReceiverError = receiverErrorFields.some((f) => getFieldState(f as any).invalid);
        const hasShipmentError = shipmentErrorFields.some((f) => getFieldState(f as any).invalid);

        const errorSections: string[] = [];
        if (hasSenderError) errorSections.push("Sender Details");
        if (hasReceiverError) errorSections.push("Receiver Information");
        if (hasShipmentError) errorSections.push("Shipment Details");

        const sectionText = errorSections.length
          ? `Please fix errors in: ${errorSections.join(", ")}`
          : "Please fill all required fields before submitting";

        toast.error(sectionText, { duration: 5000 });
        setIsSubmittingLocal(false);
        return;
      }

      await handleSubmit(handleCreate)();
    } catch (err) {
      setIsSubmittingLocal(false);
    }
  };

  const handleBack = () => step > 1 && setStep(step - 1);

  const handleCreate = async (data: OrderFormData) => {
    // shortAddress (filled by map/SPL) and nationalAddress are the same concept.
    // Always coalesce them so whichever field the user/map filled gets saved.
    const resolvedNationalAddress =
      watch("receiver.shortAddress") ||
      watch("receiver.nationalAddress") ||
      "";

    const updatedData = {
      ...data,
      type: "LM",
      receiver: {
        ...data.receiver,
        address:
          watch("receiver.address") || watch("receiver.streetName") || "",
        nationalAddress: resolvedNationalAddress,
        shortAddress: resolvedNationalAddress, // keep both in sync
      },
    };

    createOrder(updatedData, {
      onSuccess: () => {
        setShowPreview(false);
        onClose();
        toast.success("Order created successfully");
        setIsSubmittingLocal(false);
      },
      onError: (error: any) => {
        toast.error(extractErrorMessage(error, "Failed to create order"), { duration: 5000 });
        setIsSubmittingLocal(false);
      },
    });
  };

  const handleClientSelect = async (clientId: string) => {
    const sender = clients?.find((client: any) => client?.id === clientId);
    console.log(sender,'sender')
    if (!sender) return;

    // Set basic fields
    setValue("clientId", clientId);
    setValue("clientName", sender?.name);
    setValue("sender.name", sender?.name);
    setValue("sender.mobile", sender?.mobileNo);
    setValue("sender.email", sender.email);
    setValue("sender.country", sender?.country);
    setValue("sender.city", sender?.city);
    setValue("sender.district", sender.district);
    setValue("sender.countryName", sender?.countryName);
    setValue("sender.cityName", sender?.cityName);
    setValue("sender.districtName", sender.districtName);

    // WAREhouse address should be filled correctly: sender?.location contains the warehouse address
    const warehouseAddress = sender?.location || sender?.address || "";
    setValue("sender.address", warehouseAddress);

    const isSaudiArabia =
      sender?.country === "Saudi Arabia" ||
      sender?.countryName === "Saudi Arabia" ||
      sender?.country === "1";

    if (isSaudiArabia) {
      setValue("sender.shortAddress", sender?.shortAddress || "");
      setValue("sender.nationalAddress", sender?.shortAddress || "");
    } else {
      setValue("sender.shortAddress", "");
      setValue("sender.nationalAddress", "");
    }

    setValue("sender.dialCode", sender?.dialCode);

    // Set coordinates and zip code
    if (sender?.latitude && sender?.longitude) {
      setValue("sender.latitude", sender.latitude.toString());
      setValue("sender.longitude", sender.longitude.toString());
      setValue("sender.zipCode", sender.zipCode || "");
    } else if (warehouseAddress) {
      try {
        const geocoded = await populateSenderData(sender);
        setValue("sender.latitude", geocoded?.latitude || "");
        setValue("sender.longitude", geocoded?.longitude || "");
        setValue("sender.zipCode", geocoded?.zipCode || "");
      } catch (error) {
        console.error("Geocoding failed on client select:", error);
        setValue("sender.latitude", "");
        setValue("sender.longitude", "");
        setValue("sender.zipCode", "");
      }
    } else {
      setValue("sender.latitude", "");
      setValue("sender.longitude", "");
      setValue("sender.zipCode", "");
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
        <div className="rounded-2xl  xl:w-8/12 lg:w-8/12 w-11/12  relative">
          {/* Header */}
          <div className="flex items-center">
            <div className=" bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] flex items-center justify-between px-6 py-4   w-full rounded-tl-3xl rounded-tr-[63px] ">
              <h1 className="text-2xl">Create Order</h1>
            </div>
            <div className="relative ">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={38}
                height={37}
                viewBox="0 0 38 37"
                fill="none"
                className="absolute -bottom-[10px] -left-[8px] rotate-[-1.8deg]"
              >
                <path
                  d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z"
                  fill="#F7F6F2"
                />
              </svg>

              <button
                onClick={onClose}
                className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer w-12 h-12 ml-5 flex items-center justify-center flex-shrink-0"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>
          <div className="w-full space-y-4 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-tr-[30px]">
            <div className=" h-[calc(100vh-10rem)] overflow-y-auto space-y-4 pt-0 p-4 px-6 scrollbar-clean">
              <div className="bg-[#FFFFFF] p-4 mt-2 rounded-xl shadow-md">
                <SenderSection
                  clients={clients}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  watch={watch}
                  control={control}
                  useDifferentSender={useDifferentSender}
                  setUseDifferentSender={setUseDifferentSender}
                  onClientSelect={handleClientSelect}
                  setError={setError}
                />
              </div>

              <div className="bg-[#FFFFFF] p-4 rounded-xl shadow-md">
                <ReceiverSection
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  watch={watch}
                  control={control}
                  clearErrors={clearErrors}
                  setError={setError}
                />
              </div>

              <ShipmentSection
                register={register}
                errors={errors}
                setValue={setValue}
                watch={watch}
                clearErrors={clearErrors}
                mode="add"
                open={true}
                onClose={onClose}
                setMode={() => {}}
                isSubmitted={isShipmentSubmitted}
              />

              {permissions?.some(
                (permission: any) => permission?.module === "products"
              ) && (
                <div className="col-span-4 font-medium text-gray-700 mt-2 mb-1 p-4 bg-white rounded-xl">
                  <p className="text-[#212121] pb-2">Add Items</p>
                  <ShipmentItemSection
                    setValue={setValue}
                    watch={watch}
                    register={register}
                    errors={errors}
                    control={control}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-[#D8D8D8] px-6 py-4 flex justify-end space-x-2  bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-[30px] ">
            <button
              className="border border-[#D8D8D8] bg-transparent rounded-full p-2 px-4 text-base font-medium"
              onClick={step === 1 ? onClose : handleBack}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitWithValidation}
              disabled={isRequestPending || isSubmittingLocal}
              className={`${
                isRequestPending || isSubmittingLocal
                  ? "bg-gray-800 cursor-not-allowed text-[#ffffff]"
                  : "bg-[#FBD037] hover:bg-yellow-400 text-[#000000]"
              } flex items-center rounded-full p-2 px-6 space-x-1 disabled:cursor-not-allowed cursor-pointer text-base font-medium`}
            >
              {(isRequestPending || isSubmittingLocal) && (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                  />
                </svg>
              )}
              Submit
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          onClose={onClose}
          onBack={() => setShowPreview(false)}
          watch={watch}
          onSubmit={handleSubmit(handleCreate)}
          isSubmitting={isRequestPending}
        />
      )}
    </>
  );
};

export default AddOrdersModal;
